import React, { useState, useEffect, useRef } from 'react';
import MonthSelector from './MonthSelector';
import '../styles/AddPackageModal.css';

const parseList = (str) => {
  if (!str || typeof str !== 'string') return [];
  return [...new Set((str.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean)))];
};

const AddPackageModal = ({
  isOpen,
  onClose,
  packageData,
  onChange,
  onSave,
  saving,
  isEditMode = false,
  hotelOptions = [],
  flightOptions = [],
}) => {
  const [hotelsDropdownOpen, setHotelsDropdownOpen] = useState(false);
  const [flightsDropdownOpen, setFlightsDropdownOpen] = useState(false);
  const hotelsDropdownRef = useRef(null);
  const flightsDropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setHotelsDropdownOpen(false);
      setFlightsDropdownOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (hotelsDropdownRef.current && !hotelsDropdownRef.current.contains(e.target)) {
        setHotelsDropdownOpen(false);
      }
      if (flightsDropdownRef.current && !flightsDropdownRef.current.contains(e.target)) {
        setFlightsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const handleCityChange = (city, checked) => {
    const currentCities = packageData.departureCities || [];
    let updatedCities;
    if (checked) {
      updatedCities = [...currentCities, city];
    } else {
      updatedCities = currentCities.filter(c => c !== city);
    }
    onChange('departureCities', updatedCities);
  };

  const hotelsList = parseList(packageData.hotels);
  const flightsList = parseList(packageData.flights);

  const handleHotelToggle = (name, checked) => {
    const current = parseList(packageData.hotels);
    const next = checked ? [...current, name] : current.filter((h) => h !== name);
    onChange('hotels', next.join(', '));
  };

  const handleFlightToggle = (name, checked) => {
    const current = parseList(packageData.flights);
    const next = checked ? [...current, name] : current.filter((f) => f !== name);
    onChange('flights', next.join(', '));
  };

  const dropdownHotels = [...new Set([...(hotelOptions || []), ...hotelsList])];
  const dropdownFlights = [...new Set([...(flightOptions || []), ...flightsList])];

  return (
    <div className="add-package-page">
      <div className="add-package-page-inner">
        <div className="add-package-page-header">
          <h2>{isEditMode ? 'Edit Package' : 'Add New Package'}</h2>
          <button type="button" className="add-package-back-btn" onClick={onClose}>
            ← Back to packages
          </button>
        </div>

        <div className="add-package-page-body">
          <form onSubmit={(e) => { e.preventDefault(); onSave(); }}>
            <div className="form-grid">
              {/* Package Name */}
              <div className="form-group full-width">
                <label htmlFor="packageName">Package Name *</label>
                <input
                  id="packageName"
                  type="text"
                  value={packageData.packageName || ''}
                  onChange={(e) => onChange('packageName', e.target.value)}
                  className="form-input"
                  placeholder="e.g., Umrah July"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="packageType">Package Type *</label>
                <select
                  id="packageType"
                  value={packageData.packageType || 'umrah'}
                  onChange={(e) => onChange('packageType', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="umrah">Umrah</option>
                  <option value="explore_dunya">Explore Dunya</option>
                </select>
              </div>

              {/* Duration Dates */}
              <div className="form-group">
                <label htmlFor="durationFrom">Duration From *</label>
                <input
                  id="durationFrom"
                  type="date"
                  value={packageData.durationFrom || ''}
                  onChange={(e) => {
                    onChange('durationFrom', e.target.value);
                    // If durationTo is before the new durationFrom, clear it
                    if (packageData.durationTo && e.target.value) {
                      const newFromDate = new Date(e.target.value);
                      const toDate = new Date(packageData.durationTo);
                      if (toDate <= newFromDate) {
                        onChange('durationTo', '');
                      }
                    }
                  }}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="durationTo">Duration To *</label>
                <input
                  id="durationTo"
                  type="date"
                  value={packageData.durationTo || ''}
                  onChange={(e) => onChange('durationTo', e.target.value)}
                  min={packageData.durationFrom || ''}
                  className="form-input"
                  required
                />
                {packageData.durationFrom && packageData.durationTo && 
                 new Date(packageData.durationTo) <= new Date(packageData.durationFrom) && (
                  <span className="form-error">Duration To must be after Duration From</span>
                )}
              </div>

              {/* Departure Cities */}
              <div className="form-group full-width">
                <label>Departure Cities *</label>
                <div className="departure-cities-checkbox-group">
                  {['Stockholm', 'Copenhagen', 'Gothenburg'].map((city) => {
                    const cities = packageData.departureCities || [];
                    const isChecked = cities.includes(city);
                    return (
                      <label key={city} className="city-checkbox-label-modal">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleCityChange(city, e.target.checked)}
                          className="city-checkbox"
                        />
                        <span>{city}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Available Destinations / Month */}
              <div className="form-group full-width">
                <MonthSelector
                  id="availableDestinations"
                  label="Month *"
                  value={packageData.monthSelectorValue || (isEditMode && packageData.availableDestinations ? 
                    (() => {
                      // Convert month name to YYYY-MM format for MonthSelector
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
                      const monthIndex = monthNames.findIndex(m => m.toLowerCase() === (packageData.availableDestinations || packageData.month || '').toLowerCase());
                      if (monthIndex !== -1 && packageData.durationFrom) {
                        const year = new Date(packageData.durationFrom).getFullYear();
                        return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
                      }
                      return '';
                    })() : '') || ''}
                  onChange={(value) => {
                    // Store the YYYY-MM format for MonthSelector
                    onChange('monthSelectorValue', value);
                    // Also extract and store the month name for the API
                    if (value) {
                      const [, monthNum] = value.split('-');
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
                      const monthName = monthNames[parseInt(monthNum) - 1];
                      onChange('availableDestinations', monthName);
                    } else {
                      onChange('availableDestinations', '');
                    }
                  }}
                  placeholder="Select Month"
                />
              </div>

              {/* Max Bookings */}
              <div className="form-group">
                <label htmlFor="maxBookings">Max Bookings *</label>
                <input
                  id="maxBookings"
                  type="number"
                  value={packageData.maxBookings || ''}
                  onChange={(e) => onChange('maxBookings', parseInt(e.target.value))}
                  className="form-input"
                  min="1"
                  required
                />
              </div>

              {/* Status */}
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  value={packageData.status || 'active'}
                  onChange={(e) => onChange('status', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="coming-soon">Coming Soon</option>
                </select>
              </div>

              {/* Pricing Section */}
              <div className="form-section-header full-width">
                <h3>Pricing (SEK)</h3>
              </div>

              {/* Adult Prices */}
              <div className="form-group">
                <label htmlFor="adult4bed">Adult: 4-bed *</label>
                <input
                  id="adult4bed"
                  type="number"
                  value={packageData.adult4bed || ''}
                  onChange={(e) => onChange('adult4bed', parseInt(e.target.value))}
                  className="form-input"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="adult3bed">Adult: 3-bed *</label>
                <input
                  id="adult3bed"
                  type="number"
                  value={packageData.adult3bed || ''}
                  onChange={(e) => onChange('adult3bed', parseInt(e.target.value))}
                  className="form-input"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="adult2bed">Adult: 2-bed *</label>
                <input
                  id="adult2bed"
                  type="number"
                  value={packageData.adult2bed || ''}
                  onChange={(e) => onChange('adult2bed', parseInt(e.target.value))}
                  className="form-input"
                  min="0"
                  required
                />
              </div>

              {/* Child Prices */}
              <div className="form-group">
                <label htmlFor="child4bed">Children: 4-bed *</label>
                <input
                  id="child4bed"
                  type="number"
                  value={packageData.child4bed || ''}
                  onChange={(e) => onChange('child4bed', parseInt(e.target.value))}
                  className="form-input"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="child3bed">Children: 3-bed *</label>
                <input
                  id="child3bed"
                  type="number"
                  value={packageData.child3bed || ''}
                  onChange={(e) => onChange('child3bed', parseInt(e.target.value))}
                  className="form-input"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="child2bed">Children: 2-bed *</label>
                <input
                  id="child2bed"
                  type="number"
                  value={packageData.child2bed || ''}
                  onChange={(e) => onChange('child2bed', parseInt(e.target.value))}
                  className="form-input"
                  min="0"
                  required
                />
              </div>

              {/* Infant Price */}
              <div className="form-group">
                <label htmlFor="infant">Infants *</label>
                <input
                  id="infant"
                  type="number"
                  value={packageData.infant || ''}
                  onChange={(e) => onChange('infant', parseInt(e.target.value))}
                  className="form-input"
                  min="0"
                  required
                />
              </div>
            </div>

            {/* Package Image Section */}
            <div className="form-section-header full-width">
              <h3>Package Image</h3>
            </div>

            <div className="form-group full-width">
              <label htmlFor="packageImage">Package Image</label>
              <input
                id="packageImage"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    onChange('packageImage', file);
                    // Preview the image
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      onChange('imagePreview', reader.result);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                className="form-input"
              />
              {packageData.imagePreview && (
                <div style={{ marginTop: '10px' }}>
                  <img 
                    src={packageData.imagePreview} 
                    alt="Preview" 
                    style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>New image preview</p>
                </div>
              )}
              {!packageData.imagePreview && isEditMode && packageData.currentImageUrl && (
                <div style={{ marginTop: '10px' }}>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Current image:</p>
                  <img 
                    src={packageData.currentImageUrl || ''} 
                    alt="Current" 
                    style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Package Details Section */}
            <div className="form-section-header full-width">
              <h3>Package Details</h3>
            </div>

            {/* Hotels – multi-select dropdown */}
            <div className="form-group full-width" ref={hotelsDropdownRef}>
              <label>Hotels</label>
              <div className="custom-dropdown">
                <button
                  type="button"
                  className="custom-dropdown-trigger"
                  onClick={() => setHotelsDropdownOpen((o) => !o)}
                  aria-expanded={hotelsDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span className="custom-dropdown-label">
                    {hotelsList.length === 0
                      ? 'Select hotels...'
                      : `${hotelsList.length} hotel${hotelsList.length !== 1 ? 's' : ''} selected`}
                  </span>
                  <span className={`custom-dropdown-chevron ${hotelsDropdownOpen ? 'open' : ''}`}>▼</span>
                </button>
                {hotelsDropdownOpen && (
                  <div className="custom-dropdown-panel">
                    <div className="custom-dropdown-options">
                      {dropdownHotels.map((name) => {
                        const isChecked = hotelsList.includes(name);
                        return (
                          <label key={name} className="custom-dropdown-option">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleHotelToggle(name, e.target.checked)}
                              className="city-checkbox"
                            />
                            <span>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Flights – multi-select dropdown */}
            <div className="form-group full-width form-group-flights" ref={flightsDropdownRef}>
              <label>Flights</label>
              <div className="custom-dropdown">
                <button
                  type="button"
                  className="custom-dropdown-trigger"
                  onClick={() => setFlightsDropdownOpen((o) => !o)}
                  aria-expanded={flightsDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <span className="custom-dropdown-label">
                    {flightsList.length === 0
                      ? 'Select flights...'
                      : `${flightsList.length} flight${flightsList.length !== 1 ? 's' : ''} selected`}
                  </span>
                  <span className={`custom-dropdown-chevron ${flightsDropdownOpen ? 'open' : ''}`}>▼</span>
                </button>
                {flightsDropdownOpen && (
                  <div className="custom-dropdown-panel">
                    <div className="custom-dropdown-options">
                      {dropdownFlights.map((name) => {
                        const isChecked = flightsList.includes(name);
                        return (
                          <label key={name} className="custom-dropdown-option">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleFlightToggle(name, e.target.checked)}
                              className="city-checkbox"
                            />
                            <span>{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Meals Included */}
            <div className="form-section-header full-width">
              <h3>Meals Included</h3>
            </div>

            <div className="form-group full-width">
              <div className="departure-cities-checkbox-group">
                <label className="city-checkbox-label-modal">
                  <input
                    type="checkbox"
                    checked={packageData.breakfastIncluded || false}
                    onChange={(e) => onChange('breakfastIncluded', e.target.checked)}
                    className="city-checkbox"
                  />
                  <span>Breakfast</span>
                </label>
                <label className="city-checkbox-label-modal">
                  <input
                    type="checkbox"
                    checked={packageData.lunchIncluded || false}
                    onChange={(e) => onChange('lunchIncluded', e.target.checked)}
                    className="city-checkbox"
                  />
                  <span>Lunch</span>
                </label>
                <label className="city-checkbox-label-modal">
                  <input
                    type="checkbox"
                    checked={packageData.dinnerIncluded || false}
                    onChange={(e) => onChange('dinnerIncluded', e.target.checked)}
                    className="city-checkbox"
                  />
                  <span>Dinner</span>
                </label>
              </div>
            </div>

            <div className="add-package-page-footer">
              <button
                type="button"
                onClick={onClose}
                className="cancel-button-modal"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="save-button-modal"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Package'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPackageModal;


